// ApiSourceForm.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "./axiosConfig";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Table,
  Alert,
  Spinner,
  Badge,
  InputGroup,
} from "react-bootstrap";

import "./UserPanel.css";

type Organization = {
  id: number;
  name: string;
};

type MetricConfig = {
  id?: number;
  name?: string;
  metric_type?: string;
  json_path?: string;
  labels?: string;
  data_source_type?: string;

  // NEW: backend preload MetricConfigs.Organizations
  organizations?: Organization[];
};

type APISource = {
  id: number;
  url: string;
  metric_configs?: MetricConfig[];
  tenant_id?: number;
};

type TestResponse = {
  ok: boolean;
  status?: number;
  latency_ms?: number;
  error?: string;

  body?: string;
  raw?: string;

  content_type?: string;
  truncated?: boolean;
  is_json?: boolean;
  json?: any;
};

type BoolMapHint = {
  containerKey: string;
  keys: string[];
};

type Suggestion = {
  recommendedMetricType?: "gauge" | "counter";
  recommendedLabels?: string;
  candidateJsonPaths: string[];
  candidateLabelKeys: string[];
  boolMaps: BoolMapHint[];
  notes: string[];
};

type TenantMetricCurrentValueResponse = {
  tenant_id: number;
  name: string;
  value_query: string;

  result_type?: string;
  result?: any;

  raw_value?: string | null;
  mapped_value?: string | null;
  display_value?: string | null;
  mapping_applied?: boolean;
};

function safeTrim(s: string) {
  return (s ?? "").trim();
}

function parseCommaKeys(s: string): string[] {
  return safeTrim(s)
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function isPlainObject(v: any) {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function tryParseJSON(raw: string): { ok: boolean; value?: any; error?: string } {
  try {
    const v = JSON.parse(raw);
    return { ok: true, value: v };
  } catch (e: any) {
    return { ok: false, error: e?.message || "JSON parse failed" };
  }
}

/**
 * Metric Name must be snake_case:
 * - start with [a-z]
 * - only [a-z0-9_]
 * - cannot end with _
 * - cannot contain __
 */
function validateSnakeCaseMetricName(name: string): string | null {
  const v = safeTrim(name);

  if (!v) return "Metric Name is required.";

  if (!/^[a-z][a-z0-9_]*$/.test(v)) {
    return "Metric Name must be snake_case: start with a-z, only use a-z, 0-9 and underscore (_).";
  }

  if (v.endsWith("_")) {
    return "Metric Name cannot end with underscore (_).";
  }

  if (v.includes("__")) {
    return "Metric Name cannot contain consecutive underscores (__).";
  }

  return null;
}

/**
 * Collect candidate GJSON paths:
 * - number => candidate
 * - boolean => candidate + |@bool
 * - string => numeric-like only; skip datetime-like
 */
function collectCandidatePaths(
  node: any,
  prefix: string,
  out: string[],
  depth: number,
  limit: number
) {
  if (out.length >= limit) return;
  if (depth > 5) return;

  if (typeof node === "number") {
    out.push(prefix);
    return;
  }

  if (typeof node === "boolean") {
    out.push(prefix + "|@bool");
    return;
  }

  if (typeof node === "string") {
    const s = node.trim();

    // skip datetime-like strings
    if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(s)) return;

    // keep numeric-like strings
    const n = Number(s);
    if (!Number.isNaN(n) && s !== "") {
      out.push(prefix);
    }
    return;
  }

  if (Array.isArray(node)) {
    for (let i = 0; i < Math.min(node.length, 2); i++) {
      collectCandidatePaths(node[i], prefix ? `${prefix}.${i}` : `${i}`, out, depth + 1, limit);
      if (out.length >= limit) return;
    }
    return;
  }

  if (isPlainObject(node)) {
    for (const k of Object.keys(node)) {
      if (out.length >= limit) return;
      const nextPrefix = prefix ? `${prefix}.${k}` : k;
      collectCandidatePaths(node[k], nextPrefix, out, depth + 1, limit);
    }
  }
}

function looksBoolMap(obj: any): { ok: boolean; keys: string[] } {
  if (!isPlainObject(obj)) return { ok: false, keys: [] };

  const keys = Object.keys(obj);
  if (keys.length === 0) return { ok: false, keys: [] };

  let good = 0;
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "boolean") good++;
    else if (typeof v === "number") good++;
    else if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (s === "true" || s === "false" || s === "1" || s === "0") good++;
    }
  }
  return { ok: good / keys.length >= 0.8, keys };
}

function collectCandidateLabelKeys(parsed: any): string[] {
  if (!isPlainObject(parsed)) return [];

  const preferred = ["host", "site", "region", "env", "dc", "pop", "carrier", "vendor", "service", "name"];
  const out: string[] = [];
  const seen = new Set<string>();

  const push = (k: string) => {
    if (!k) return;
    if (seen.has(k)) return;
    seen.add(k);
    out.push(k);
  };

  for (const k of preferred) {
    if (typeof parsed[k] === "string" && parsed[k].trim()) push(k);
  }

  for (const k of Object.keys(parsed)) {
    if (out.length >= 10) break;
    if (/(time|date|timestamp)/i.test(k)) continue;
    if (typeof parsed[k] === "string" && parsed[k].trim()) push(k);
  }

  return out.slice(0, 10);
}

function guessMetricTypeByName(metricName: string): "gauge" | "counter" {
  const n = safeTrim(metricName).toLowerCase();
  if (/(total|count|requests|bytes|sum|events)/.test(n)) return "counter";
  return "gauge";
}

function buildSuggestion(parsed: any, currentMetricName: string): Suggestion {
  const suggestion: Suggestion = {
    candidateJsonPaths: [],
    candidateLabelKeys: [],
    boolMaps: [],
    notes: [],
  };

  const candidates: string[] = [];
  collectCandidatePaths(parsed, "", candidates, 0, 20);

  const boolMapPathSet = new Set<string>();

  if (isPlainObject(parsed)) {
    for (const k of Object.keys(parsed)) {
      const v = parsed[k];
      const bm = looksBoolMap(v);
      if (bm.ok) {
        const keys = bm.keys.filter((x) => x && x.length <= 32).slice(0, 20);
        suggestion.boolMaps.push({ containerKey: k, keys });

        for (const kk of keys.slice(0, 20)) {
          boolMapPathSet.add(`${k}.${kk}|@bool`);
        }
      }
    }
  }

  const unique = Array.from(new Set(candidates.filter(Boolean)));
  suggestion.candidateJsonPaths = unique.filter((p) => !boolMapPathSet.has(p)).slice(0, 12);

  suggestion.candidateLabelKeys = collectCandidateLabelKeys(parsed);
  suggestion.recommendedMetricType = guessMetricTypeByName(currentMetricName);

  if (suggestion.candidateLabelKeys.length > 0) {
    suggestion.recommendedLabels = suggestion.candidateLabelKeys.slice(0, 2).join(",");
  }

  suggestion.notes.push("Suggestions are heuristic and work for any JSON response shape (not limited to a specific schema).");
  suggestion.notes.push("json_path uses GJSON (tidwall/gjson), NOT JSONPath.");
  suggestion.notes.push('For boolean-like values, append "|@bool" to map true/false cleanly (e.g., checks.ok|@bool).');
  suggestion.notes.push("Time/date fields are ignored for metric candidates and label suggestions.");

  return suggestion;
}

function getBodyFromTestResponse(tr: TestResponse | null): string {
  if (!tr) return "";
  if (typeof tr.body === "string" && tr.body.trim()) return tr.body;
  if (typeof tr.raw === "string" && tr.raw.trim()) return tr.raw;
  return "";
}

function formatPreviewText(tr: TestResponse | null): string {
  if (!tr) return "";

  if (tr.is_json && tr.json !== undefined && tr.json !== null) {
    try {
      return JSON.stringify(tr.json, null, 2);
    } catch {
      // ignore
    }
  }

  const body = getBodyFromTestResponse(tr);
  if (!body) return "";

  const parsed = tryParseJSON(body);
  if (parsed.ok) {
    try {
      return JSON.stringify(parsed.value, null, 2);
    } catch {
      return body;
    }
  }

  return body;
}

function prettyJSON(v: any): string {
  if (v === undefined || v === null) return "";
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

/* =========================
   NEW: Label whitelist
   ========================= */

const LABEL_WHITELIST: { key: string; desc: string }[] = [
  { key: "host", desc: "Host / Domain" },
  { key: "site", desc: "Site" },
  { key: "region", desc: "Region" },
  { key: "env", desc: "Environment" },
  { key: "dc", desc: "Data Center" },
  { key: "pop", desc: "POP" },
  { key: "carrier", desc: "Carrier" },
  { key: "vendor", desc: "Vendor" },
  { key: "service", desc: "Service" },
  { key: "name", desc: "Name" },
  { key: "target", desc: "Target" },
  // If you need, you can add more fixed keys here, but keep it SMALL.
];

const LABEL_WHITELIST_SET = new Set(LABEL_WHITELIST.map((x) => x.key));

// Optional guardrail
const MAX_LABELS = 4;

const ApiSourceForm: React.FC = () => {
  const [url, setUrl] = useState("");
  const [metricName, setMetricName] = useState("");
  const [metricType, setMetricType] = useState<"gauge" | "counter">("gauge");

  const [jsonPath, setJsonPath] = useState("");

  // KEEP: backend wants string labels, but we DO NOT allow free input anymore
  const [labels, setLabels] = useState("");

  // NEW: whitelist multi-select state
  const [selectedLabelKeys, setSelectedLabelKeys] = useState<string[]>([]);

  const [apiSources, setApiSources] = useState<APISource[]>([]);
  const [alert, setAlert] = useState<{ variant: string; msg: string } | null>(null);

  // Organizations (NEW)
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  // ✅ refs to always get latest typed values (avoid stale state on fast submit)
  const metricNameRef = useRef<HTMLInputElement | null>(null);
  const urlRef = useRef<HTMLInputElement | null>(null);
  const jsonPathRef = useRef<HTMLInputElement | null>(null);

  const [metricNameTouched, setMetricNameTouched] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState<TestResponse | null>(null);
  const [testParsed, setTestParsed] = useState<any | null>(null);

  const [rowTesting, setRowTesting] = useState<Record<number, boolean>>({});
  const [rowTestRes, setRowTestRes] = useState<Record<number, TestResponse | null>>({});

  // Row Query (NEW) - query current value from Prometheus
  const [rowQuerying, setRowQuerying] = useState<Record<number, boolean>>({});
  const [rowQueryRes, setRowQueryRes] = useState<Record<number, TenantMetricCurrentValueResponse | null>>({});

  // Expand (NEW) - allow expand either test or query
  const [expandedRow, setExpandedRow] = useState<{ id: number; kind: "test" | "query" } | null>(null);

  const suggestion = useMemo<Suggestion | null>(() => {
    if (!testParsed) return null;
    return buildSuggestion(testParsed, metricName);
  }, [testParsed, metricName]);

  // For whitelist labels, we don't need to disable, but we can show hint
  const labelsOptionalHint = useMemo(() => {
    if (!testRes?.ok) return false;
    if (!suggestion) return false;
    return (suggestion.candidateLabelKeys?.length ?? 0) === 0;
  }, [testRes, suggestion]);

  const metricNameError = useMemo(() => {
    if (!metricNameTouched) return null;
    return validateSnakeCaseMetricName(metricName);
  }, [metricName, metricNameTouched]);

  const fetchApiSources = async () => {
    try {
      const res = await axios.get("/api/api-sources");
      const list = res.data?.data || res.data || [];
      setApiSources(Array.isArray(list) ? list : []);
    } catch {
      setAlert({ variant: "danger", msg: "Failed to load API sources" });
    }
  };

  const loadOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      const res = await axios.get("/api/organizations");
      const list: Organization[] = res.data?.organizations || res.data || [];
      setOrgs(Array.isArray(list) ? list : []);
    } catch {
      setAlert({ variant: "danger", msg: "Failed to load organizations" });
      setOrgs([]);
    } finally {
      setLoadingOrgs(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
    fetchApiSources();
  }, []);

  // NEW: Keep labels string in sync with whitelist selection
  useEffect(() => {
    const cleaned = selectedLabelKeys
      .map((x) => safeTrim(x))
      .filter(Boolean)
      .filter((x) => LABEL_WHITELIST_SET.has(x));

    const unique = Array.from(new Set(cleaned)).slice(0, MAX_LABELS);
    if (unique.join(",") !== labels) {
      setLabels(unique.join(","));
    }
    if (unique.join(",") !== selectedLabelKeys.join(",")) {
      setSelectedLabelKeys(unique);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLabelKeys]);

  const handleTest = async () => {
    const currentUrl = safeTrim(urlRef.current?.value ?? url);
    if (!currentUrl) {
      setAlert({ variant: "warning", msg: "Please input API URL before testing." });
      return;
    }

    setTesting(true);
    setTestRes(null);
    setTestParsed(null);
    setAlert(null);

    try {
      const res = await axios.post("/api/api-sources/test", { url: currentUrl });
      const data: TestResponse = res.data;

      setTestRes(data);

      if (!data?.ok) {
        setAlert({
          variant: "danger",
          msg: data?.error ? `Test failed: ${data.error}` : "Test failed",
        });
        return;
      }

      if (data?.is_json && data?.json !== undefined && data?.json !== null) {
        setTestParsed(data.json);
        return;
      }

      const body = getBodyFromTestResponse(data);
      if (body) {
        const parsed = tryParseJSON(body);
        if (parsed.ok) setTestParsed(parsed.value);
        else {
          setAlert({
            variant: "warning",
            msg: `Test OK, but response is not JSON or cannot be parsed: ${parsed.error}`,
          });
        }
      } else {
        setAlert({
          variant: "info",
          msg: "Test OK. (Tip) Return 'raw' or 'body' in /api/api-sources/test response to show preview.",
        });
      }
    } catch (e: any) {
      setAlert({
        variant: "danger",
        msg: e?.response?.data?.error || "Failed to test API source",
      });
    } finally {
      setTesting(false);
    }
  };

  const applyBoolMapKey = (containerKey: string, key: string) => {
    setJsonPath(`${containerKey}.${key}|@bool`);
    setMetricType("gauge");
  };

  const normalizeIds = (ids: number[]) => [...ids].sort((a, b) => a - b);

  const renderOrgMultiSelect = (
    value: number[],
    onChange: (ids: number[]) => void,
    placeholder = "Please select at least one organization"
  ) => (
    <Form.Group className="mb-2">
      <Form.Label style={{ fontWeight: 600 }}>Organizations</Form.Label>
      <Form.Select
        multiple
        value={value.map(String)}
        onChange={(e) => {
          const options = Array.from(e.target.selectedOptions).map((o) => Number(o.value));
          onChange(options);
        }}
        disabled={loadingOrgs}
      >
        {orgs.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </Form.Select>

      <div className="mt-2" aria-live="polite">
        {value.length ? (
          normalizeIds(value)
            .map((id) => orgs.find((o) => o.id === id)?.name || String(id))
            .map((name, idx) => (
              <Badge bg="secondary" key={`${name}-${idx}`} className="me-1">
                {name}
              </Badge>
            ))
        ) : (
          <small className="text-muted">{placeholder}</small>
        )}
      </div>

      <Form.Text className="text-muted">At least one organization is required for Create.</Form.Text>
    </Form.Group>
  );

  /* =========================
     NEW: Whitelist labels UI
     ========================= */

  const onChangeLabelWhitelist = (keys: string[]) => {
    const cleaned = keys
      .map((x) => safeTrim(x))
      .filter(Boolean)
      .filter((x) => LABEL_WHITELIST_SET.has(x));

    const unique = Array.from(new Set(cleaned)).slice(0, MAX_LABELS);

    if (cleaned.length > MAX_LABELS) {
      setAlert({ variant: "warning", msg: `Too many labels. Max allowed is ${MAX_LABELS}.` });
    }

    setSelectedLabelKeys(unique);
  };

  const addLabelKey = (k: string) => {
    const key = safeTrim(k);
    if (!key) return;

    if (!LABEL_WHITELIST_SET.has(key)) {
      setAlert({ variant: "warning", msg: `Label "${key}" is not in whitelist. Please use whitelist labels only.` });
      return;
    }

    const next = Array.from(new Set([...selectedLabelKeys, key])).slice(0, MAX_LABELS);
    if (next.length === selectedLabelKeys.length && selectedLabelKeys.includes(key)) return;

    if (next.length >= MAX_LABELS && !selectedLabelKeys.includes(key)) {
      setAlert({ variant: "warning", msg: `Max labels is ${MAX_LABELS}. Remove one before adding.` });
      return;
    }

    setSelectedLabelKeys(next);
  };

  const removeLabelKey = (k: string) => {
    const key = safeTrim(k);
    setSelectedLabelKeys(selectedLabelKeys.filter((x) => x !== key));
  };

  const renderWhitelistLabels = () => (
    <Form.Group>
      <Form.Label style={{ fontWeight: 600 }}>labels (Whitelist only)</Form.Label>

      <Form.Select
        multiple
        value={selectedLabelKeys}
        onChange={(e) => {
          const keys = Array.from(e.target.selectedOptions).map((o) => o.value);
          onChangeLabelWhitelist(keys);
        }}
      >
        {LABEL_WHITELIST.map((x) => (
          <option key={x.key} value={x.key}>
            {x.key} — {x.desc}
          </option>
        ))}
      </Form.Select>

      <div className="mt-2" aria-live="polite">
        {selectedLabelKeys.length ? (
          selectedLabelKeys.map((k) => (
            <Badge
              key={k}
              bg="secondary"
              className="me-1"
              style={{ cursor: "pointer" }}
              title="Click to remove"
              onClick={() => removeLabelKey(k)}
            >
              {k} ✕
            </Badge>
          ))
        ) : (
          <small className="text-muted">
            {labelsOptionalHint ? "No suitable label keys detected from test. Labels are optional." : "Select labels from whitelist (optional)."}
          </small>
        )}
      </div>

      <Form.Text className="text-muted">
        Labels are <b>restricted to whitelist</b> to prevent dynamic/high-cardinality labels. Max {MAX_LABELS}.
        <span className="ms-2">Current: <code>{labels || "(empty)"}</code></span>
      </Form.Text>

      {/* Hidden field: keep backward compatibility if you need to inspect in DOM */}
      <input type="hidden" value={labels} readOnly />
    </Form.Group>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const currentUrl = safeTrim(urlRef.current?.value ?? url);
    const currentName = safeTrim(metricNameRef.current?.value ?? metricName);
    const currentJsonPath = safeTrim(jsonPathRef.current?.value ?? jsonPath);

    // IMPORTANT: labels come from whitelist selection ONLY
    const currentLabels = selectedLabelKeys.join(",");

    setMetricNameTouched(true);

    if (!currentUrl || !currentName) {
      setAlert({ variant: "warning", msg: "API URL and Metric Name are required" });
      return;
    }

    if (!selectedOrgIds.length) {
      setAlert({ variant: "warning", msg: "Please select at least one organization before Create." });
      return;
    }

    const nameErr = validateSnakeCaseMetricName(currentName);
    if (nameErr) {
      setAlert({ variant: "warning", msg: nameErr });
      return;
    }

    if (!currentJsonPath) {
      setAlert({
        variant: "warning",
        msg: "json_path is required (GJSON path). Tip: test the API first to get suggestions.",
      });
      return;
    }

    // Guardrail: double check whitelist (in case someone hacks DOM)
    const bad = parseCommaKeys(currentLabels).find((k) => !LABEL_WHITELIST_SET.has(k));
    if (bad) {
      setAlert({ variant: "danger", msg: `Invalid label "${bad}". Only whitelist labels are allowed.` });
      return;
    }

    try {
      await axios.post("/api/api-sources", {
        url: currentUrl,
        metric_name: currentName,
        metric_type: metricType,
        json_path: currentJsonPath,
        labels: currentLabels, // whitelist only
        organization_ids: selectedOrgIds,
      });

      setAlert({ variant: "success", msg: "API Source created (metric registered immediately)" });

      setUrl("");
      setMetricName("");
      setMetricNameTouched(false);
      setMetricType("gauge");
      setJsonPath("");

      setSelectedLabelKeys([]);
      setLabels("");

      setSelectedOrgIds([]);

      setTestRes(null);
      setTestParsed(null);

      fetchApiSources();
    } catch (err: any) {
      setAlert({
        variant: "danger",
        msg: err?.response?.data?.error || "Failed to create API Source",
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this API Source?")) return;

    try {
      await axios.delete(`/api/api-sources/${id}`);

      setRowTestRes((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
      setRowTesting((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });

      setRowQueryRes((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
      setRowQuerying((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });

      if (expandedRow?.id === id) setExpandedRow(null);

      fetchApiSources();
    } catch {
      setAlert({ variant: "danger", msg: "Failed to delete API Source" });
    }
  };

  const handleRowTest = async (s: APISource) => {
    const id = s.id;

    setRowTesting((prev) => ({ ...prev, [id]: true }));
    setRowTestRes((prev) => ({ ...prev, [id]: null }));
    setAlert(null);

    try {
      const res = await axios.post("/api/api-sources/test", { url: safeTrim(s.url) });
      const data: TestResponse = res.data;

      setRowTestRes((prev) => ({ ...prev, [id]: data }));
      setExpandedRow({ id, kind: "test" });
    } catch (e: any) {
      setRowTestRes((prev) => ({
        ...prev,
        [id]: { ok: false, error: e?.response?.data?.error || "Failed to test API source" },
      }));
      setExpandedRow({ id, kind: "test" });
    } finally {
      setRowTesting((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleRowQuery = async (s: APISource) => {
    const id = s.id;
    const mc = s.metric_configs?.[0];
    const name = safeTrim(mc?.name || "");

    if (!name) {
      setAlert({ variant: "warning", msg: "This API Source has no metric name to query." });
      return;
    }

    setRowQuerying((prev) => ({ ...prev, [id]: true }));
    setRowQueryRes((prev) => ({ ...prev, [id]: null }));
    setAlert(null);

    try {
      const res = await axios.get<TenantMetricCurrentValueResponse>("/api/tenant/metric-current-value", {
        params: { name },
      });
      setRowQueryRes((prev) => ({ ...prev, [id]: res.data }));
      setExpandedRow({ id, kind: "query" });
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || "Query failed";
      setAlert({ variant: "danger", msg: `Query failed: ${msg}` });
      setExpandedRow({ id, kind: "query" });
    } finally {
      setRowQuerying((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleRowUse = (s: APISource) => {
    setUrl(s.url);

    const mc = s.metric_configs?.[0];

    setMetricName("");
    setMetricNameTouched(false);

    if (mc?.metric_type === "counter" || mc?.metric_type === "gauge") setMetricType(mc.metric_type);
    if (mc?.json_path) setJsonPath(mc.json_path);

    // NEW: prefill organizations from first metric_config if backend returns it
    const orgIds = (mc?.organizations || []).map((o) => o.id).filter((x) => typeof x === "number");
    setSelectedOrgIds(orgIds);

    // NEW: prefill labels ONLY from whitelist
    const rawLabels = parseCommaKeys(mc?.labels || "");
    const wl = rawLabels.filter((k) => LABEL_WHITELIST_SET.has(k)).slice(0, MAX_LABELS);
    setSelectedLabelKeys(wl);
    setLabels(wl.join(","));

    setTestRes(null);
    setTestParsed(null);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderMetricSummary = (s: APISource) => {
    const mc = s.metric_configs?.[0];
    if (!mc) return <span className="text-muted">-</span>;

    const orgNames = (mc.organizations || []).map((o) => o?.name).filter(Boolean).slice(0, 4);

    const labelKeys = parseCommaKeys(mc.labels || "").filter((k) => LABEL_WHITELIST_SET.has(k)).slice(0, 6);

    return (
      <div style={{ lineHeight: 1.4 }}>
        <div>
          <code>{mc.name || "-"}</code>{" "}
          <Badge bg="secondary" className="ms-1">
            {mc.metric_type || "gauge"}
          </Badge>
        </div>

        {orgNames.length > 0 && (
          <div className="mt-1">
            {orgNames.map((n, i) => (
              <Badge bg="secondary" key={`${mc.name}-org-${i}`} className="me-1">
                {n}
              </Badge>
            ))}
          </div>
        )}

        {labelKeys.length > 0 && (
          <div className="mt-1">
            {labelKeys.map((k) => (
              <Badge bg="dark" key={`${mc.name}-label-${k}`} className="me-1">
                {k}
              </Badge>
            ))}
          </div>
        )}

        <div className="text-muted" style={{ fontSize: 12 }}>
          <div>
            <span style={{ fontWeight: 600 }}>json_path:</span> <code>{mc.json_path || "-"}</code>
          </div>
          <div>
            <span style={{ fontWeight: 600 }}>labels:</span> <code>{mc.labels || "-"}</code>
          </div>
        </div>
      </div>
    );
  };

  const renderRowTestSummaryBadges = (tr: TestResponse | null) => {
    if (!tr) return null;
    return (
      <>
        <Badge bg={tr.ok ? "success" : "danger"} className="me-2">
          {tr.ok ? "OK" : "FAIL"}
        </Badge>
        {typeof tr.status === "number" && (
          <Badge bg="secondary" className="me-2">
            HTTP {tr.status}
          </Badge>
        )}
        {typeof tr.latency_ms === "number" && (
          <Badge bg="secondary" className="me-2">
            {tr.latency_ms} ms
          </Badge>
        )}
        {tr.content_type && (
          <Badge bg="secondary" className="me-2">
            {tr.content_type}
          </Badge>
        )}
        {tr.truncated && (
          <Badge bg="warning" text="dark" className="me-2">
            truncated
          </Badge>
        )}
      </>
    );
  };

  const renderRowQueryBadges = (qr: TenantMetricCurrentValueResponse | null) => {
    if (!qr) return null;
    const dv = safeTrim(String(qr.display_value ?? ""));
    return (
      <>
        <Badge bg="primary" className="me-2">
          PROM
        </Badge>
        <Badge bg={dv ? "success" : "warning"} text={dv ? undefined : "dark"} className="me-2">
          {dv ? `value: ${dv}` : "no data"}
        </Badge>
        {qr.mapping_applied ? (
          <Badge bg="success" className="me-2">
            mapped
          </Badge>
        ) : (
          <Badge bg="secondary" className="me-2">
            raw
          </Badge>
        )}
        {qr.result_type ? <Badge bg="secondary" className="me-2">{qr.result_type}</Badge> : null}
      </>
    );
  };

  const renderRowTestPreview = (id: number) => {
    const tr = rowTestRes[id] || null;
    if (!tr) return null;

    const preview = formatPreviewText(tr);

    return (
      <tr>
        <td colSpan={5} style={{ background: "#0b1220" }}>
          <div className="p-3 border rounded" style={{ background: "#0b1220", borderColor: "#1f2a44" }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div style={{ fontWeight: 700, color: "#e6edf3" }}>Test Result</div>
              <div className="d-flex align-items-center gap-2">
                {renderRowTestSummaryBadges(tr)}
                <Button size="sm" variant="outline-light" type="button" onClick={() => setExpandedRow(null)}>
                  Close
                </Button>
              </div>
            </div>

            {!tr.ok && tr.error && <Alert variant="danger" className="mb-2">{tr.error}</Alert>}

            {tr.ok && !preview && (
              <Alert variant="info" className="mb-2">
                Test OK. (Tip) Return <code>raw</code> or <code>body</code> in /api/api-sources/test response to show preview.
              </Alert>
            )}

            {preview && (
              <div className="mt-2">
                <div className="mb-1" style={{ fontWeight: 600, color: "#e6edf3" }}>
                  Response Preview
                </div>
                <pre
                  style={{
                    maxHeight: 220,
                    overflow: "auto",
                    background: "#0f172a",
                    color: "#e6edf3",
                    padding: 12,
                    borderRadius: 6,
                    border: "1px solid #1f2a44",
                    marginBottom: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {preview}
                </pre>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const renderRowQueryPreview = (id: number) => {
    const qr = rowQueryRes[id] || null;

    return (
      <tr>
        <td colSpan={5} style={{ background: "#0b1220" }}>
          <div className="p-3 border rounded" style={{ background: "#0b1220", borderColor: "#1f2a44" }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div style={{ fontWeight: 700, color: "#e6edf3" }}>Prometheus Query Result</div>
              <div className="d-flex align-items-center gap-2">
                {renderRowQueryBadges(qr)}
                <Button size="sm" variant="outline-light" type="button" onClick={() => setExpandedRow(null)}>
                  Close
                </Button>
              </div>
            </div>

            {!qr && <Alert variant="info" className="mb-2">No query result loaded.</Alert>}

            {qr && (
              <>
                <div style={{ fontSize: 12, color: "#e6edf3", opacity: 0.8 }} className="mb-1">
                  Metric Name: <code>{qr.name}</code>
                </div>

                <div className="mb-2">
                  <div className="mb-1" style={{ fontWeight: 600, color: "#e6edf3" }}>
                    Value Query (PromQL)
                  </div>
                  <pre
                    style={{
                      maxHeight: 160,
                      overflow: "auto",
                      background: "#0f172a",
                      color: "#e6edf3",
                      padding: 12,
                      borderRadius: 6,
                      border: "1px solid #1f2a44",
                      marginBottom: 0,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {qr.value_query || ""}
                  </pre>
                </div>

                <div className="mb-2">
                  <div className="mb-1" style={{ fontWeight: 600, color: "#e6edf3" }}>
                    Raw / Mapped / Display
                  </div>
                  <div style={{ color: "#e6edf3" }}>
                    <div>raw_value: <code>{String(qr.raw_value ?? "")}</code></div>
                    <div>mapped_value: <code>{String(qr.mapped_value ?? "")}</code></div>
                    <div>display_value: <code>{String(qr.display_value ?? "")}</code></div>
                  </div>
                </div>

                <div className="mb-2">
                  <div className="mb-1" style={{ fontWeight: 600, color: "#e6edf3" }}>
                    Prometheus Result (debug)
                  </div>
                  <pre
                    style={{
                      maxHeight: 220,
                      overflow: "auto",
                      background: "#0f172a",
                      color: "#e6edf3",
                      padding: 12,
                      borderRadius: 6,
                      border: "1px solid #1f2a44",
                      marginBottom: 0,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {prettyJSON(qr.result)}
                  </pre>
                </div>
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const mainPreview = useMemo(() => formatPreviewText(testRes), [testRes]);

  return (
    <Container className="mt-4">
      <h4 className="mb-3">API Source Configuration</h4>

      {alert && (
        <Alert variant={alert.variant} dismissible onClose={() => setAlert(null)}>
          {alert.msg}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col md={8}>
            <Form.Group>
              <Form.Label>API URL</Form.Label>

              <InputGroup>
                <Form.Control
                  ref={urlRef}
                  list="api-url-list"
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/health"
                  required
                />
                <Button
                  variant="outline-primary"
                  type="button"
                  onClick={handleTest}
                  disabled={testing}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {testing ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Testing
                    </>
                  ) : (
                    "Test"
                  )}
                </Button>
              </InputGroup>

              <datalist id="api-url-list">
                {apiSources.map((s) => (
                  <option key={s.id} value={s.url} />
                ))}
              </datalist>

              <Form.Text className="text-muted">
                Tip: Click <b>Test</b> to preview response (if backend returns raw/body) and get GJSON suggestions.
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Metric Type</Form.Label>
              <Form.Select value={metricType} onChange={(e) => setMetricType(e.target.value as "gauge" | "counter")}>
                <option value="gauge">gauge</option>
                <option value="counter">counter</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Gauge for status/latency/value; Counter only for monotonically increasing totals.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        {/* Organizations */}
        <Row className="mb-3">
          <Col md={12}>{renderOrgMultiSelect(selectedOrgIds, setSelectedOrgIds)}</Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Metric Name</Form.Label>
              <Form.Control
                ref={metricNameRef}
                name="metric_name"
                type="text"
                value={metricName}
                onChange={(e) => {
                  setMetricName(e.target.value);
                  if (!metricNameTouched) setMetricNameTouched(true);
                }}
                onBlur={() => setMetricNameTouched(true)}
                placeholder="eu_port_443_up"
                required
                pattern="[a-z][a-z0-9_]*"
                isInvalid={!!metricNameError}
              />
              <Form.Control.Feedback type="invalid">{metricNameError}</Form.Control.Feedback>
              <Form.Text className="text-muted">
                Must be snake_case (e.g. <code>eu_port_443_up</code>).
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={5}>
            <Form.Group>
              <Form.Label>json_path (GJSON path)</Form.Label>
              <Form.Control
                ref={jsonPathRef}
                type="text"
                value={jsonPath}
                onChange={(e) => setJsonPath(e.target.value)}
                placeholder="e.g. ports.443|@bool"
                required
              />
              <Form.Text className="text-muted">
                This is <b>GJSON</b> path (tidwall/gjson), NOT JSONPath. Use <code>|@bool</code> for boolean-like values.
              </Form.Text>
            </Form.Group>
          </Col>

          {/* NEW: Whitelist labels */}
          <Col md={3}>{renderWhitelistLabels()}</Col>
        </Row>

        {(testRes || suggestion) && (
          <div className="mb-3 p-3 border rounded">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div style={{ fontWeight: 700 }}>Test Result</div>
            </div>

            {testRes && (
              <div className="mb-2">
                <Badge bg={testRes.ok ? "success" : "danger"} className="me-2">
                  {testRes.ok ? "OK" : "FAIL"}
                </Badge>
                {typeof testRes.status === "number" && (
                  <Badge bg="secondary" className="me-2">
                    HTTP {testRes.status}
                  </Badge>
                )}
                {typeof testRes.latency_ms === "number" && (
                  <Badge bg="secondary" className="me-2">
                    {testRes.latency_ms} ms
                  </Badge>
                )}
                {testRes.content_type && (
                  <Badge bg="secondary" className="me-2">
                    {testRes.content_type}
                  </Badge>
                )}
                {testRes.truncated && (
                  <Badge bg="warning" text="dark" className="me-2">
                    truncated
                  </Badge>
                )}
                {labelsOptionalHint && (
                  <Badge bg="secondary" className="ms-2">
                    labels optional
                  </Badge>
                )}
              </div>
            )}

            {suggestion && suggestion.notes.length > 0 && (
              <Alert variant="info" className="mb-2">
                <div style={{ fontWeight: 700 }} className="mb-1">
                  Guidance (GJSON)
                </div>
                <ul className="mb-0">
                  {suggestion.notes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {suggestion && suggestion.boolMaps.length > 0 && (
              <div className="mb-2">
                <div className="mb-1" style={{ fontWeight: 600 }}>
                  Detected boolean-map keys:
                </div>

                {suggestion.boolMaps.slice(0, 3).map((bm) => (
                  <div key={bm.containerKey} className="mb-2">
                    <div className="text-muted" style={{ fontSize: 12, marginBottom: 6 }}>
                      container: <code>{bm.containerKey}</code>{" "}
                      <span className="ms-2">example json_path:</span>{" "}
                      <code>
                        {bm.containerKey}.{bm.keys[0] || "key"}|@bool
                      </code>
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                      {bm.keys.slice(0, 12).map((k) => (
                        <Button
                          key={k}
                          size="sm"
                          variant="outline-primary"
                          type="button"
                          onClick={() => applyBoolMapKey(bm.containerKey, k)}
                        >
                          Use {bm.containerKey}.{k}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {suggestion && suggestion.candidateJsonPaths.length > 0 && (
              <div className="mb-2">
                <div className="mb-1" style={{ fontWeight: 600 }}>
                  Candidate json_path:
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {suggestion.candidateJsonPaths.slice(0, 10).map((p) => (
                    <Button
                      key={p}
                      size="sm"
                      variant="outline-secondary"
                      type="button"
                      onClick={() => setJsonPath(p)}
                      title="Click to use this json_path"
                    >
                      {p}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Candidate label keys => ONLY allow whitelist */}
            {suggestion && suggestion.candidateLabelKeys.length > 0 && (
              <div className="mb-2">
                <div className="mb-1" style={{ fontWeight: 600 }}>
                  Candidate label keys (whitelist only):
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {suggestion.candidateLabelKeys.slice(0, 10).map((k) => {
                    const allowed = LABEL_WHITELIST_SET.has(k);
                    return (
                      <Button
                        key={k}
                        size="sm"
                        variant={allowed ? "outline-secondary" : "outline-dark"}
                        type="button"
                        onClick={() => (allowed ? addLabelKey(k) : setAlert({ variant: "warning", msg: `Label "${k}" is not in whitelist.` }))}
                        title={allowed ? "Click to add this label key" : "Not allowed (not in whitelist)"}
                        disabled={!allowed}
                      >
                        + {k} {allowed ? "" : "(blocked)"}
                      </Button>
                    );
                  })}
                </div>
                <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                  Only whitelist labels can be used to avoid dynamic/high-cardinality labels.
                </div>
              </div>
            )}

            {mainPreview && (
              <div className="mt-2">
                <div className="mb-1" style={{ fontWeight: 600 }}>
                  Response Preview
                </div>
                <pre
                  style={{
                    maxHeight: 220,
                    overflow: "auto",
                    background: "#0f172a",
                    color: "#e6edf3",
                    padding: 12,
                    borderRadius: 6,
                    border: "1px solid #1f2a44",
                    marginBottom: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {mainPreview}
                </pre>
              </div>
            )}
          </div>
        )}

        <Button
          variant="primary"
          type="submit"
          disabled={!!metricNameError || selectedOrgIds.length === 0}
          title={selectedOrgIds.length === 0 ? "Please select at least one organization" : ""}
        >
          Create API Source
        </Button>
      </Form>

      <hr className="my-4" />

      <Table striped bordered hover size="sm" responsive variant="dark">
        <thead>
          <tr>
            <th style={{ width: 60 }}>#</th>
            <th>API URL</th>
            <th style={{ width: 460 }}>Metric (first metric_config)</th>
            <th style={{ width: 260 }}>Test / Query</th>
            <th style={{ width: 190 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {apiSources.map((s, idx) => {
            const isTesting = !!rowTesting[s.id];
            const tr = rowTestRes[s.id] || null;

            const isQuerying = !!rowQuerying[s.id];
            const qr = rowQueryRes[s.id] || null;

            const isExpandedTest = expandedRow?.id === s.id && expandedRow?.kind === "test";
            const isExpandedQuery = expandedRow?.id === s.id && expandedRow?.kind === "query";

            return (
              <React.Fragment key={s.id}>
                <tr>
                  <td>{idx + 1}</td>
                  <td>{s.url}</td>
                  <td>
                    {renderMetricSummary(s)}
                    <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                      (Tip) Metric Name must be typed manually and must be snake_case.
                    </div>
                  </td>

                  <td>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        type="button"
                        onClick={() => handleRowTest(s)}
                        disabled={isTesting}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {isTesting ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Testing
                          </>
                        ) : (
                          "Test"
                        )}
                      </Button>

                      <Button
                        variant="outline-success"
                        size="sm"
                        type="button"
                        onClick={() => handleRowQuery(s)}
                        disabled={isQuerying}
                        style={{ whiteSpace: "nowrap" }}
                        title="Query current value from Prometheus using /api/tenant/metric-current-value"
                      >
                        {isQuerying ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Querying
                          </>
                        ) : (
                          "Query"
                        )}
                      </Button>

                      {tr && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          type="button"
                          onClick={() => setExpandedRow(isExpandedTest ? null : { id: s.id, kind: "test" })}
                        >
                          {isExpandedTest ? "Hide Test" : "View Test"}
                        </Button>
                      )}

                      {qr && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          type="button"
                          onClick={() => setExpandedRow(isExpandedQuery ? null : { id: s.id, kind: "query" })}
                        >
                          {isExpandedQuery ? "Hide Query" : "View Query"}
                        </Button>
                      )}
                    </div>

                    <div className="mt-2" style={{ fontSize: 12 }}>
                      {tr && (
                        <span className="me-2">
                          <Badge bg={tr.ok ? "success" : "danger"} className="me-1">
                            {tr.ok ? "OK" : "FAIL"}
                          </Badge>
                          {typeof tr.status === "number" && (
                            <Badge bg="secondary" className="me-1">
                              {tr.status}
                            </Badge>
                          )}
                          {typeof tr.latency_ms === "number" && (
                            <Badge bg="secondary" className="me-1">
                              {tr.latency_ms}ms
                            </Badge>
                          )}
                        </span>
                      )}

                      {qr && <span>{renderRowQueryBadges(qr)}</span>}
                    </div>
                  </td>

                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-success"
                        size="sm"
                        type="button"
                        onClick={() => handleRowUse(s)}
                        title="Use this API URL and its first metric_config to prefill the form (Metric Name is NOT filled)"
                      >
                        Use
                      </Button>

                      <Button variant="danger" size="sm" onClick={() => handleDelete(s.id)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>

                {isExpandedTest && renderRowTestPreview(s.id)}
                {isExpandedQuery && renderRowQueryPreview(s.id)}
              </React.Fragment>
            );
          })}

          {!apiSources.length && (
            <tr>
              <td colSpan={5} className="text-center text-muted">
                No API sources configured
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Container>
  );
};

export default ApiSourceForm;
